import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Calendar, Car, Check, X } from 'lucide-react';
import API from '../services/api';

const MesReservations = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [onglet, setOnglet] = useState('recues');
  const [reservations, setReservations] = useState([]);
  const [mesReservations, setMesReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    chargerTout();
  }, [user]);

  const chargerTout = async () => {
    try {
      const [recues, mes] = await Promise.all([
        API.get('/locations/reservations/recues'),
        API.get('/locations/reservations/mes-reservations')
      ]);
      setReservations(recues.data.reservations);
      setMesReservations(mes.data.reservations);
    } finally {
      setLoading(false);
    }
  };

  const handleTraiter = async (id, statut) => {
    await API.put(`/locations/reservations/${id}/traiter`, { statut });
    chargerTout();
  };

  const badgeStatut = (statut) => {
    const styles = {
      en_attente: 'bg-yellow-100 text-yellow-600',
      confirme: 'bg-green-100 text-green-600',
      refuse: 'bg-red-100 text-red-600',
      termine: 'bg-gray-100 text-gray-600',
      annule: 'bg-gray-100 text-gray-500'
    };
    return styles[statut] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-white text-2xl font-black flex items-center gap-2">
            <Calendar size={24} style={{ color: '#F5A623' }} />
            Réservations
          </h1>
          <p style={{ color: '#F5A623' }} className="text-sm mt-1">
            Gérez vos demandes de location
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Onglets */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setOnglet('recues')}
            style={{
              background: onglet === 'recues' ? '#F5A623' : 'white',
              color: onglet === 'recues' ? '#000' : '#6b7280',
              border: onglet === 'recues' ? 'none' : '1px solid #e5e7eb'
            }}
            className="px-4 py-2 rounded-xl font-bold text-sm hover:opacity-90 transition"
          >
            Demandes reçues ({reservations.length})
          </button>
          <button
            onClick={() => setOnglet('envoyees')}
            style={{
              background: onglet === 'envoyees' ? '#F5A623' : 'white',
              color: onglet === 'envoyees' ? '#000' : '#6b7280',
              border: onglet === 'envoyees' ? 'none' : '1px solid #e5e7eb'
            }}
            className="px-4 py-2 rounded-xl font-bold text-sm hover:opacity-90 transition"
          >
            Mes demandes ({mesReservations.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-400">Chargement...</div>
        ) : (
          <>
            {/* Réservations reçues */}
            {onglet === 'recues' && (
              <div className="space-y-4">
                {reservations.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                    <Calendar size={60} className="mx-auto mb-4 text-gray-200" />
                    <p className="text-gray-400">Aucune demande reçue</p>
                  </div>
                ) : (
                  reservations.map(r => (
                    <div key={r.id} className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">{r.titre}</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Par : <strong>{r.locataire_prenom} {r.locataire_nom}</strong>
                            {r.locataire_tel && ` — ${r.locataire_tel}`}
                          </p>
                          <div className="flex gap-4 mt-2 text-sm text-gray-600">
                            <span>📅 Du {new Date(r.date_debut).toLocaleDateString('fr-FR')}</span>
                            <span>au {new Date(r.date_fin).toLocaleDateString('fr-FR')}</span>
                            <span>({r.nb_jours} jours)</span>
                          </div>
                          <p className="text-sm font-bold text-gray-900 mt-1">
                            Total : {parseInt(r.prix_total).toLocaleString('fr-FR')} FCFA
                          </p>
                          {r.message && (
                            <p className="text-sm text-gray-500 mt-2 italic">"{r.message}"</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${badgeStatut(r.statut)}`}>
                            {r.statut}
                          </span>
                          {r.statut === 'en_attente' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleTraiter(r.id, 'confirme')}
                                className="flex items-center gap-1 bg-green-100 text-green-600 px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-green-200 transition"
                              >
                                <Check size={14} /> Confirmer
                              </button>
                              <button
                                onClick={() => handleTraiter(r.id, 'refuse')}
                                className="flex items-center gap-1 bg-red-100 text-red-500 px-3 py-1.5 rounded-xl text-xs font-bold hover:bg-red-200 transition"
                              >
                                <X size={14} /> Refuser
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Mes demandes envoyées */}
            {onglet === 'envoyees' && (
              <div className="space-y-4">
                {mesReservations.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                    <Car size={60} className="mx-auto mb-4 text-gray-200" />
                    <p className="text-gray-400">Aucune réservation effectuée</p>
                  </div>
                ) : (
                  mesReservations.map(r => (
                    <div key={r.id} className="bg-white rounded-2xl shadow-sm p-5 border border-gray-100">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900">{r.titre}</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Propriétaire : <strong>{r.vendeur_prenom} {r.vendeur_nom}</strong>
                          </p>
                          <div className="flex gap-4 mt-2 text-sm text-gray-600">
                            <span>📅 Du {new Date(r.date_debut).toLocaleDateString('fr-FR')}</span>
                            <span>au {new Date(r.date_fin).toLocaleDateString('fr-FR')}</span>
                            <span>({r.nb_jours} jours)</span>
                          </div>
                          <p className="text-sm font-bold text-gray-900 mt-1">
                            Total : {parseInt(r.prix_total).toLocaleString('fr-FR')} FCFA
                          </p>
                          {r.caution && (
                            <p className="text-xs text-gray-500">
                              Caution : {parseInt(r.caution).toLocaleString('fr-FR')} FCFA
                            </p>
                          )}
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${badgeStatut(r.statut)}`}>
                          {r.statut}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MesReservations;